import React from "react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Short name of the area, e.g. "the composer" — shown in the fallback. */
  label?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render/lifecycle errors in its subtree so one broken screen shows a
 * message instead of blanking the whole app. The error text is surfaced on
 * screen (and logged) to make failures diagnosable in place.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error, info.componentStack);
  }

  private reset = (): void => this.setState({ error: null });

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        className="mx-auto max-w-2xl rounded-2xl border-2 border-[#EF4444] bg-[#FEE2E2] p-5"
      >
        <p className="text-base font-extrabold text-[#7F1D1D]">
          Something broke{this.props.label ? ` in ${this.props.label}` : ""}.
        </p>
        <p className="mt-2 break-words font-mono text-sm text-[#B91C1C]">
          {error.message || "Unknown error"}
        </p>
        <button
          type="button"
          onClick={this.reset}
          className="mt-3 rounded-xl border-2 border-[#B91C1C] bg-white px-4 py-2 text-sm font-extrabold text-[#B91C1C] transition-colors hover:bg-[#FEF2F2]"
        >
          Try again
        </button>
      </div>
    );
  }
}
